"use client";
import dynamic from "next/dynamic";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// Lazy-loaded: this provider wraps the whole app (via AppWrappers) but
// StripeReaderConnect only ever renders inside a user-triggered modal
// (`isConnectionModalOpen`). Statically importing it pulled the companion
// roster / Stripe-terminal tree into every account route's first-load bundle,
// inflating LCP render-delay on routes that never use a physical card reader.
// See plans/account-lcp-render-delay.md.
const StripeReaderConnect = dynamic(
  () =>
    import(
      "@/app/directories/companion/(main)/roster/clientComponents/StripeReaderConnect"
    ).then((m) => m.StripeReaderConnect),
  { ssr: false },
);
import type Stripe from "stripe";
import {
  getKioskPIN,
  getPracticeConfigurationFromActivationCode,
  removeKioskPIN,
  setKioskPINCookie,
} from "../actions/actions";
import {
  cancelPOSPaymentByReaderId,
  getTerminalByReaderId,
  processPOSPaymentByReaderId,
} from "../actions/stripe-actions";
import { useInterval } from "../hooks/useInterval";

// Declaing the types/interfaces for Context Store
// type StripeReaderStatus = 'in_progress' | 'failed' | 'succeeded';

interface StripeReaderContextType {
  stripeReaderId?: string | null;
  setStripeReaderId: (stripeReaderId?: string) => void;

  stripeReader?: Stripe.Terminal.Reader | null;
  setStripeReader: (stripeReader?: Stripe.Terminal.Reader) => void;

  connectToReader: (activationCode: string) => Promise<number | null>;
  disconnectFromReader: (activationCode: string) => Promise<string | null>;

  resetStripeReaderContext: () => Promise<string | null>;

  processPaymentIntent: (
    paymentIntentId: string,
  ) => Promise<Stripe.Response<Stripe.Terminal.Reader>>;
  cancelTransaction: () => Promise<{ status: string }>;

  setIsConnectionModalOpen: (isConnectionModalOpen: boolean) => void;

  isLoading: boolean;
  isOnline: boolean;
}

// Create i18n context object
export const StripeReaderContext = createContext<
  StripeReaderContextType | undefined
>(undefined);

export const useStripeReaderContext = () => {
  const stripeReaderContext = useContext(StripeReaderContext);
  if (stripeReaderContext === undefined) {
    throw new Error(
      "useStripeReaderContext must be within StripeReaderProvider",
    );
  }

  return stripeReaderContext;
};

interface StripeReaderProviderProps {
  children: ReactNode;
}

// Create Provider component
export const StripeReaderProvider = ({
  children,
}: StripeReaderProviderProps) => {
  const [stripeReaderId, setStripeReaderId] = useState<
    string | undefined | null
  >();
  const [stripeReader, setStripeReader] = useState<
    Stripe.Terminal.Reader | null | undefined
  >();
  const [isConnectionModalOpen, setIsConnectionModalOpen] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchSavedKioskPin = async () => {
      const kioskPin = await getKioskPIN();
      if (kioskPin) {
        const practiceConfiguration =
          await getPracticeConfigurationFromActivationCode(kioskPin);
        if (practiceConfiguration) {
          setStripeReaderId(practiceConfiguration.posDeviceId);
          setIsLoading(false);
          const stringifiedReader = await getTerminalByReaderId(
            practiceConfiguration.posDeviceId,
          );
          if (stringifiedReader) {
            const reader = JSON.parse(stringifiedReader);
            setStripeReader(reader);
            setKioskPINCookie(kioskPin);
          }
        } else {
          removeKioskPIN();
          setIsLoading(false);
        }
      }
      setIsLoading(false);
    };

    fetchSavedKioskPin();
  }, []);

  const pollDelay = useMemo(() => {
    if (stripeReaderId || stripeReader?.action?.status === "in_progress") {
      if (stripeReader && stripeReader.action?.status === "in_progress") {
        return 1000;
      }
      return 5000;
    }
    return null;
  }, [stripeReaderId, stripeReader]);

  useInterval(async () => {
    if (stripeReaderId) {
      const stringifiedReader = await getTerminalByReaderId(stripeReaderId);
      const reader = stringifiedReader && JSON.parse(stringifiedReader);
      const haveAnyRelevantFieldsChanged =
        reader?.status !== stripeReader?.status ||
        reader?.id !== stripeReader?.id ||
        reader?.action?.process_payment_intent?.payment_intent !==
          stripeReader?.action?.process_payment_intent?.payment_intent ||
        reader?.action?.status !== stripeReader?.action?.status;

      if (haveAnyRelevantFieldsChanged) {
        setStripeReader(reader);
      }
    }
  }, pollDelay);

  const connectToReader = async (
    activationCode: string,
  ): Promise<number | null> => {
    const practiceConfiguration =
      await getPracticeConfigurationFromActivationCode(activationCode);
    if (practiceConfiguration) {
      setStripeReaderId(practiceConfiguration.posDeviceId);
      const stringifiedReader = await getTerminalByReaderId(
        practiceConfiguration.posDeviceId,
      );

      if (stringifiedReader) {
        const reader = JSON.parse(stringifiedReader);
        setStripeReader(reader);
        setKioskPINCookie(activationCode);
      }
      return practiceConfiguration.practiceId;
    }
    return null;
  };

  const disconnectFromReader = async (
    activationCode: string,
  ): Promise<string | null> => {
    const practiceConfiguration =
      await getPracticeConfigurationFromActivationCode(activationCode);
    if (
      practiceConfiguration &&
      stripeReader?.id === practiceConfiguration.posDeviceId
    ) {
      cancelTransaction();
      setStripeReaderId(null);
      setStripeReader(undefined);
      removeKioskPIN();
      return "Success";
    }
    return null;
  };

  const resetStripeReaderContext = async (): Promise<string | null> => {
    try {
      cancelTransaction();
      setStripeReaderId(null);
      setStripeReader(undefined);
      setIsConnectionModalOpen(false);
      removeKioskPIN();
      setIsLoading(false);
      return "Success";
    } catch (error) {
      return null;
    }
  };

  const processPaymentIntent = async (paymentIntentId: string) => {
    if (!stripeReader) {
      throw new Error("No reader detected");
    }
    if (stripeReader.status !== "online") {
      throw new Error("Stripe Reader is offline");
    }
    try {
      return await processPOSPaymentByReaderId(
        paymentIntentId,
        stripeReader.id,
      );
    } catch (error) {
      throw new Error("Failed to process payment intent");
    }
  };

  const cancelTransaction = async (): Promise<{ status: string }> => {
    if (!stripeReader || stripeReader.status !== "online") {
      return new Promise((resolve) => resolve({ status: "SUCCESS" }));
    }

    return await cancelPOSPaymentByReaderId(stripeReader.id);
  };

  return (
    <StripeReaderContext.Provider
      value={{
        stripeReaderId,
        setStripeReaderId,
        stripeReader,
        setStripeReader,
        connectToReader,
        disconnectFromReader,
        resetStripeReaderContext,
        processPaymentIntent,
        cancelTransaction,
        setIsConnectionModalOpen,
        isLoading,
        isOnline: stripeReader?.status === "online",
      }}
    >
      {children}
      {isConnectionModalOpen && <StripeReaderConnect />}
    </StripeReaderContext.Provider>
  );
};
